import { NgIf, CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzFormControlComponent, NzFormDirective, NzFormItemComponent } from 'ng-zorro-antd/form';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzOptionComponent, NzSelectComponent } from 'ng-zorro-antd/select';
import { AuthorizeDirective } from '../../../../shared/authorization/authorize.directive.';
import { ButtonComponent } from '@ppl/ui';
import { DatepickerComponent } from '@ppl/ui';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { UserService } from '../../../../services/user.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';

const fields = [
  { field: 'Timeframe', show: true, default: '30 days' },
  { field: 'status', show: true },
  { field: 'consumer', show: true },
  { field: 'consumerPplId', show: true },
  { field: 'pplPurchaseId', show: true },
  { field: 'serviceCode', show: true },
  { field: 'crmNumber', show: true }
];

@Component({
  selector: 'ppl-purchase-request-filters',
  standalone: true,
  imports: [
    ButtonComponent,
    NzColDirective,
    NzFormControlComponent,
    NzFormDirective,
    NzFormItemComponent,
    NzOptionComponent,
    NzRowDirective,
    NzSelectComponent,
    ReactiveFormsModule,
    NgIf,
    AuthorizeDirective,
    NzAutocompleteModule,
    NzDatePickerModule,
    DatepickerComponent,
    CommonModule
  ],
  templateUrl: './purchase-request-filters.component.html',
  styleUrl: './purchase-request-filters.component.css'
})
export class PurchaseRequestFiltersComponent {
  @Input() filterFields: any[] = fields;
  @Input() includeBusinessStatus: any[] = [];
  @Input() processCodes: any[] = [];
  @Input() includeProcessingStatus: any[] = [];
  @Input() chequeNumbers: any[] = [];
  @Input() export = false;
  @Output() searchEvent = new EventEmitter<any>();
  @Output() exportEvent = new EventEmitter<any>();
  filteredConsumers: any[] = [];
  filteredProcessStatusList: LookupValueItem[] = [];
  filteredProviders: any[] = [];
  @Input() pageId: string = '';
  processStatusList: LookupValueItem[] = [];
  programName!: string;
  purchaseRequestSearchForm!: FormGroup;
  showErrors = false;
  statusList: LookupValueItem[] = [];
  tabId: string = '';
  purchaseRequestServiceCodes: LookupValueItem[] = [];

  purchaseRequestStatusList: LookupValueItem[] = [
    { lkpValueCode: 'COMPLETED', lkpValueName: 'Completed' },
    { lkpValueCode: 'REJECTED', lkpValueName: 'Rejected' },
    { lkpValueCode: 'SUBMITTED', lkpValueName: 'Submitted' }
  ];

  loopback = [
    { label: '30 days', val: this.getDateBefore(30) },
    { label: '60 days', val: this.getDateBefore(60) },
    { label: '90 days', val: this.getDateBefore(90) },
    { label: '120 days', val: this.getDateBefore(120) },
    { label: '150 days', val: this.getDateBefore(150) },
    { label: '180 days', val: this.getDateBefore(180) },
    { label: '1 year', val: this.getDateBefore(365) },
    { label: '2 years', val: this.getDateBefore(730) },
    { label: '3 years', val: this.getDateBefore(1095) },
  ];

  status = [
    { id: '1', name: 'Approved' },
    { id: '2', name: 'Rejected' },
    { id: '3', name: 'In Review' },
  ];

  constructor(
    private fb: FormBuilder,
    private timesheetService: TimesheetService,
    private lookupService: LookupService,
    private dateTimeFormatter: DateTimeFormatterService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    const formFields = this.filterFields.reduce((acc, f) => {
      if (f.show) {
        if (f.field === 'Timeframe') {
          acc = { ...acc, [f.field]: [this.getDateBefore(30)] };
        }
        else {
          acc = { ...acc, [f.field]: [f.default ? f.default : null] };
        }
      }
      return acc;
    }, {});
    this.purchaseRequestSearchForm = this.fb.group(formFields);

    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    const businessStatusList = this.lookupService.getLookupValue('Business Status')
    this.statusList = this.includeBusinessStatus.length ? businessStatusList.filter((status: any) => {
      return this.includeBusinessStatus.includes(status.lkpValueCode);
    }) : businessStatusList;

    const processingStatusList = this.lookupService.getLookupValue('TS_App_S');
    this.processStatusList = this.includeProcessingStatus.length ? processingStatusList.filter((status: any) => {
      return this.includeProcessingStatus.includes(status.lkpValueCode);
    }) : processingStatusList;
    this.filteredProcessStatusList = processingStatusList.filter(status => status.lkpValueCode === 'SB' || status.lkpValueCode === 'PE'
      || status.lkpValueCode === 'TP' || status.lkpValueCode === 'SE' || status.lkpValueCode === 'AC');

    this.purchaseRequestServiceCodes = this.lookupService.getLookupValue('purchaseRequest_filter_serviceCode');
  }

  getDateBefore(days: number): string {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toLocaleDateString('en-US');
  }

  search() {
    this.showErrors = false;
    this.searchEvent.emit(this.purchaseRequestSearchForm.value);
  }

  exportToExcel() {
    this.showErrors = false;
    this.exportEvent.emit(this.purchaseRequestSearchForm.value);
  }

  onSearchChequeClick() {
    this.showErrors = false;
    if (this.purchaseRequestSearchForm.get('consumerPplId')?.value && this.purchaseRequestSearchForm.get('consumerPplId')?.value.trim() &&
      this.purchaseRequestSearchForm.get('providerPplId')?.value && this.purchaseRequestSearchForm.get('providerPplId')?.value.trim()) {
      this.searchEvent.emit({ ...this.purchaseRequestSearchForm.value, "chequeLookUp": true });
    } else {
      this.showErrors = true;
    }
  }

  disabledFutureDates = (current: Date): boolean => {
    const serviceDateConfiguration = this.filterFields?.find(entry => entry.field === 'serviceDate');
    if (serviceDateConfiguration?.hideFuturePeriods) {
      const today = new Date();
      return current.getTime() > today.getTime();
    }

    return false;
  };
}
